import {
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  useGetProjectMembersQuery,
  useRemoveProjectMemberMutation,
  useUpdateProjectTeamMutation,
} from "../../../../../api/projects"
import { useParams } from "react-router-dom"
import { IUpdateProjectTeamMemberInput } from "../../../../../api/types"
import { extractApiErrorMessage } from "../../../../../utils/api"
import { SortOrderField, clampSortOrder, normalizeSortOrderFromApi } from "./SortOrderField"
import { editProjectFieldSx, editProjectSelectFieldSx } from "../editProjectFieldSx"

const ROLE_LABEL_MAX_LEN = 40
const MAX_FEATURED_NON_COAUTHORS = 4

type RemoveTarget = {
  userId: number
  title: string
}

function countFeaturedNonCoauthors(members: IUpdateProjectTeamMemberInput[]): number {
  return members.filter(
    (item) => item.team_role !== 'coauthor' && item.is_featured
  ).length
}

export const EditTeamForm = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = Number(id)
  const { data: members = [], isLoading } = useGetProjectMembersQuery(
    { projectId },
    { skip: !id || Number.isNaN(projectId) }
  )
  const [updateProjectTeam, updateState] = useUpdateProjectTeamMutation()
  const [removeProjectMember, removeState] = useRemoveProjectMemberMutation()
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null)

  const authorMember = useMemo(
    () => members.find((member) => member.is_author) ?? null,
    [members]
  )
  const editableMembers = useMemo(
    () => members.filter((member) => !member.is_author),
    [members]
  )

  const [draft, setDraft] = useState<IUpdateProjectTeamMemberInput[]>([])
  const [authorRoleLabel, setAuthorRoleLabel] = useState<string | null>(null)
  const [authorIsFeatured, setAuthorIsFeatured] = useState<boolean | null>(null)
  const [authorSortOrder, setAuthorSortOrder] = useState<number | null>(null)

  const values: IUpdateProjectTeamMemberInput[] = draft.length > 0 ? draft : editableMembers.map((member) => ({
    user_id: member.user_id,
    team_role: member.team_role === 'coauthor' ? 'coauthor' : 'member',
    role_label: member.role_label,
    is_featured: member.is_featured,
    sort_order: normalizeSortOrderFromApi(member.sort_order),
  }))

  const patchRow = (userId: number, patch: Partial<IUpdateProjectTeamMemberInput>) => {
    setDraft((prev) => {
      const base = prev.length > 0 ? prev : values
      return base.map((item) => {
        if (item.user_id !== userId) {
          return item
        }

        return {
          ...item,
          ...patch,
          team_role: (patch.team_role ?? item.team_role) as 'member' | 'coauthor',
        }
      })
    })
  }

  const featuredNonCoauthorsCount = useMemo(
    () => countFeaturedNonCoauthors(values),
    [values]
  )

  const handleSave = async () => {
    if (featuredNonCoauthorsCount > MAX_FEATURED_NON_COAUTHORS) {
      toast.error(
        `В превью можно показать не более ${MAX_FEATURED_NON_COAUTHORS} участников без роли «Соавтор».`
      )
      return
    }

    const resolvedAuthorRoleLabel = (authorRoleLabel ?? authorMember?.role_label ?? '')?.trim() || null
    const resolvedAuthorIsFeatured = authorIsFeatured ?? authorMember?.is_featured ?? true
    const resolvedAuthorSortOrder = clampSortOrder(
      authorSortOrder ?? normalizeSortOrderFromApi(authorMember?.sort_order)
    )
    try {
      await updateProjectTeam({
        projectId,
        body: {
          author_display: {
            role_label: resolvedAuthorRoleLabel,
            is_featured: resolvedAuthorIsFeatured,
            sort_order: resolvedAuthorSortOrder,
          },
          members: values.map((item) => ({
            ...item,
            role_label: item.team_role === 'coauthor' ? null : (item.role_label?.trim() || null),
          })),
        },
      }).unwrap()
      toast.success('Команда обновлена')
      setDraft([])
      setAuthorRoleLabel(resolvedAuthorRoleLabel)
      setAuthorIsFeatured(resolvedAuthorIsFeatured)
      setAuthorSortOrder(resolvedAuthorSortOrder)
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить команду')
    }
  }

  const handleConfirmRemove = async () => {
    if (!removeTarget) {
      return
    }

    try {
      await removeProjectMember({ projectId, userId: removeTarget.userId }).unwrap()
      setDraft((prev) => prev.filter((item) => item.user_id !== removeTarget.userId))
      setRemoveTarget(null)
      toast.success('Участник исключён из проекта')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось исключить участника')
    }
  }

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2.5 }}>
      <Stack gap={2}>
        <Typography variant="black-16-semiBold">Команда проекта</Typography>
        <Typography variant="black-14-light" color="text.secondary">
          В превью — до {MAX_FEATURED_NON_COAUTHORS} участников без роли «Соавтор». Автор и соавторы настраиваются отдельно.
          «Соавтор» — публичный статус в команде (отображение), не права администратора. Права и ответственность
          назначаются во вкладке «Администрирование».
        </Typography>

        {isLoading && <Typography>Загрузка участников…</Typography>}
        {!isLoading && !authorMember && values.length === 0 && (
          <Typography variant="black-14-light">Активных участников пока нет.</Typography>
        )}

        {!isLoading && authorMember && (
          <Stack gap={1} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
            <Typography variant="black-14-medium">Вы (автор)</Typography>
            <Stack gap={0.5}>
              <Typography variant="black-14-medium">
                {(authorMember.user.fullname ?? authorMember.user.shortname ?? '').trim() || 'Пользователь'}
              </Typography>
            </Stack>
            <TextField
              size="small"
              placeholder="Ваша роль в проекте"
              value={authorRoleLabel ?? authorMember.role_label ?? ''}
              onChange={(event) => setAuthorRoleLabel(event.target.value.slice(0, ROLE_LABEL_MAX_LEN))}
              fullWidth
              sx={editProjectFieldSx}
              inputProps={{
                'aria-label': 'Ваша роль в проекте',
              }}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
              <SortOrderField
                value={clampSortOrder(
                  authorSortOrder ?? normalizeSortOrderFromApi(authorMember.sort_order)
                )}
                onChange={setAuthorSortOrder}
                sx={{ ...editProjectFieldSx, width: { xs: '100%', md: 160 } }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={authorIsFeatured ?? authorMember.is_featured}
                    onChange={(event) => setAuthorIsFeatured(event.target.checked)}
                  />
                }
                label="Показывать в превью"
              />
            </Stack>
          </Stack>
        )}

        {!isLoading && values.map((item) => {
          const member = editableMembers.find((x) => x.user_id === item.user_id)
          const title = (member?.user.fullname ?? member?.user.shortname ?? '').trim() || `ID ${item.user_id}`
          const isCoauthor = item.team_role === 'coauthor'
          const canEnableFeatured =
            item.is_featured
            || featuredNonCoauthorsCount < MAX_FEATURED_NON_COAUTHORS
          return (
            <Stack
              key={item.user_id}
              gap={1}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}
            >
              <Typography variant="black-14-medium">{title}</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                <TextField
                  select
                  size="small"
                  value={item.team_role}
                  onChange={(event) =>
                    patchRow(item.user_id, {
                      team_role: event.target.value as 'member' | 'coauthor',
                      role_label: event.target.value === 'coauthor' ? null : item.role_label,
                    })
                  }
                  fullWidth
                  sx={editProjectSelectFieldSx}
                  SelectProps={{
                    MenuProps: {
                      disableScrollLock: true,
                    },
                  }}
                  inputProps={{
                    'aria-label': 'Роль команды',
                  }}
                >
                  <MenuItem value="member">Участник</MenuItem>
                  <MenuItem value="coauthor">
                    <Tooltip title="Соавтор — публичный статус в команде, не права администратора">
                      <span>Соавтор</span>
                    </Tooltip>
                  </MenuItem>
                </TextField>
                <TextField
                  size="small"
                  placeholder="Подпись роли"
                  value={item.role_label ?? ''}
                  onChange={(event) =>
                    patchRow(item.user_id, { role_label: event.target.value.slice(0, ROLE_LABEL_MAX_LEN) })
                  }
                  disabled={isCoauthor}
                  fullWidth
                  sx={editProjectFieldSx}
                />
                <SortOrderField
                  value={normalizeSortOrderFromApi(item.sort_order)}
                  onChange={(sort_order) => patchRow(item.user_id, { sort_order })}
                  sx={{ ...editProjectFieldSx, width: { xs: '100%', md: 160 } }}
                />
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                flexWrap="wrap"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={item.is_featured}
                      onChange={(event) => patchRow(item.user_id, { is_featured: event.target.checked })}
                      disabled={isCoauthor || (!item.is_featured && !canEnableFeatured)}
                    />
                  }
                  label="Показывать в превью"
                />
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => setRemoveTarget({ userId: item.user_id, title })}
                  disabled={removeState.isLoading}
                >
                  Убрать
                </Button>
              </Stack>
            </Stack>
          )
        })}

        <Stack direction="row" spacing={1}>
          <Button variant="follow" onClick={handleSave} disabled={updateState.isLoading}>
            {updateState.isLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Сохранение...</span>
              </Stack>
            ) : (
              'Сохранить'
            )}
          </Button>
        </Stack>
      </Stack>

      <Dialog open={removeTarget !== null} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Убрать из команды</DialogTitle>
        <DialogContent>
          <Typography variant="black-14-normal">
            {removeTarget
              ? `Участник «${removeTarget.title}» будет исключён из проекта. Продолжить?`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="follow" onClick={() => setRemoveTarget(null)} disabled={removeState.isLoading}>
            Отмена
          </Button>
          <Button variant="danger" onClick={() => void handleConfirmRemove()} disabled={removeState.isLoading}>
            {removeState.isLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>Исключаем...</span>
              </Stack>
            ) : (
              'Убрать'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
